import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Calendar, MapPin, Clock, Users, Utensils, Wine, 
  Building2, Sparkles, ArrowRight, ExternalLink, Star,
  GraduationCap, Lightbulb, Globe2
} from 'lucide-react';

const ExperiencePage = () => {
  const mitTourHighlights = [
    { icon: Lightbulb, title: "City Science Lab", description: "Explore cutting-edge urban research and smart city technologies" },
    { icon: GraduationCap, title: "Expert-Led Tour", description: "Guided by MIT Media Lab, City Science researchers working on housing solutions" },
    { icon: Globe2, title: "Innovation Showcase", description: "See AI and quantum computing applications in real estate" }
  ];

  const dinnerHighlights = [
    { icon: Wine, title: "Reception & Cocktails", description: "Network with industry leaders over fine wine" },
    { icon: Utensils, title: "Gourmet Dinner", description: "Multi-course dining experience at Harvard Faculty Club" },
    { icon: Users, title: "Intimate Setting", description: "Limited to 50 distinguished guests" }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 md:px-12 lg:px-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-secondary/10" />
        <div className="relative z-10 max-w-6xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card text-sm font-medium mb-6">
            <Sparkles className="w-4 h-4 text-secondary" />
            <span>April 16-17, 2026</span>
          </div>
          <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
            The <span className="text-gradient-quantum">Experience</span>
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
            Beyond the conference: An exclusive two-day journey through Boston's premier academic institutions, 
            culminating in an intimate Chairman's Dinner.
          </p>
        </div>
      </section>

      {/* Timeline Overview */}
      <section className="py-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Day 1 Card */}
            <div className="glass-card rounded-3xl p-8 relative overflow-hidden group hover:border-primary/50 transition-all">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-primary/20 to-transparent rounded-bl-full" />
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Thursday</p>
                    <p className="text-2xl font-bold">April 16, 2026</p>
                  </div>
                </div>
                <h3 className="font-outfit text-xl font-semibold mb-2">MIT Media Lab Tour & Chairman's Dinner</h3>
                <p className="text-muted-foreground mb-4">
                  An afternoon exploring MIT Media Lab, City Science followed by an exclusive dinner at Harvard Faculty Club.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 rounded-full bg-primary/10 text-primary text-sm">MIT Media Lab, City Science</span>
                  <span className="px-3 py-1 rounded-full bg-secondary/10 text-secondary text-sm">Harvard Faculty Club</span>
                </div>
              </div>
            </div>

            {/* Day 2 Card */}
            <div className="glass-card rounded-3xl p-8 relative overflow-hidden group hover:border-primary/50 transition-all">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-secondary/20 to-transparent rounded-bl-full" />
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-secondary/20 flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-secondary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Friday</p>
                    <p className="text-2xl font-bold">April 17, 2026</p>
                  </div>
                </div>
                <h3 className="font-outfit text-xl font-semibold mb-2">Quantum Housing Conference</h3>
                <p className="text-muted-foreground mb-4">
                  Full-day conference at Harvard University featuring world-class speakers and groundbreaking research.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 rounded-full bg-primary/10 text-primary text-sm">Harvard University</span>
                  <span className="px-3 py-1 rounded-full bg-secondary/10 text-secondary text-sm">Tsai Auditorium</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MIT City Science Tour */}
      <section className="py-20 px-6 md:px-12 lg:px-24 bg-gradient-to-b from-transparent via-primary/5 to-transparent">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
                <Clock className="w-4 h-4" /> 2:00 PM - 5:00 PM
              </div>
              <h2 className="font-outfit text-3xl md:text-4xl font-bold mb-6">
                MIT Media Lab, City Science Tour
              </h2>
              <p className="text-lg text-muted-foreground mb-6">
                Experience the future of urban innovation at MIT Media Lab, City Science. 
                Our expert-led tour showcases groundbreaking research in smart cities, 
                sustainable housing, and AI-powered urban planning.
              </p>
              
              <div className="space-y-4 mb-8">
                {mitTourHighlights.map((item, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-semibold">{item.title}</h4>
                      <p className="text-sm text-muted-foreground">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="glass-card rounded-xl p-4 flex items-center gap-4">
                <MapPin className="w-5 h-5 text-primary" />
                <div>
                  <p className="font-medium">MIT Media Lab, City Science</p>
                  <p className="text-sm text-muted-foreground">75 Amherst St, Cambridge, MA 02139</p>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="aspect-[4/3] rounded-3xl overflow-hidden glass-card">
                <img 
                  src="https://images.unsplash.com/photo-1562774053-701939374585?w=800&q=80" 
                  alt="MIT Media Lab, City Science" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <p className="text-white font-outfit text-xl font-semibold">MIT Media Lab, City Science</p>
                  <p className="text-white/80 text-sm">Where innovation meets urban science</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Chairman's Dinner */}
      <section className="py-20 px-6 md:px-12 lg:px-24">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1 relative">
              <div className="aspect-[4/3] rounded-3xl overflow-hidden glass-card">
                <img 
                  src="https://images.unsplash.com/photo-1519167758481-83f550bb49b3?w=800&q=80" 
                  alt="Elegant Dinner Setting" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <p className="text-white font-outfit text-xl font-semibold">Harvard Faculty Club</p>
                  <p className="text-white/80 text-sm">An evening of distinction</p>
                </div>
              </div>
            </div>

            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary/10 text-secondary text-sm font-medium mb-4">
                <Clock className="w-4 h-4" /> 6:30 PM - 10:00 PM
              </div>
              <h2 className="font-outfit text-3xl md:text-4xl font-bold mb-6">
                Chairman's Dinner
              </h2>
              <p className="text-lg text-muted-foreground mb-6">
                An exclusive evening gathering at the prestigious Harvard Faculty Club. 
                Join thought leaders, policymakers, and industry pioneers for an intimate 
                dinner celebrating innovation in housing.
              </p>
              
              <div className="space-y-4 mb-8">
                {dinnerHighlights.map((item, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-secondary/20 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-secondary" />
                    </div>
                    <div>
                      <h4 className="font-semibold">{item.title}</h4>
                      <p className="text-sm text-muted-foreground">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="glass-card rounded-xl p-4 mb-6">
                <div className="flex items-center gap-4 mb-3">
                  <MapPin className="w-5 h-5 text-secondary" />
                  <div>
                    <p className="font-medium">Harvard Faculty Club</p>
                    <p className="text-sm text-muted-foreground">20 Quincy St, Cambridge, MA 02138</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <Star className="w-5 h-5 text-secondary" />
                  <p className="text-sm text-muted-foreground">Dress Code: Business Formal</p>
                </div>
              </div>

              <div className="p-4 rounded-xl bg-secondary/10 border border-secondary/30">
                <p className="text-sm">
                  <strong className="text-secondary">Featured Speakers:</strong> Dr. Chris Herbert, 
                  Dr. Peter Linneman, Vernand Morency, and distinguished guests.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Conference Day Preview */}
      <section className="py-20 px-6 md:px-12 lg:px-24 bg-gradient-to-b from-transparent via-secondary/5 to-transparent">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="font-outfit text-3xl md:text-4xl font-bold mb-6">
            Conference Day Highlights
          </h2>
          <p className="text-lg text-muted-foreground mb-12 max-w-3xl mx-auto">
            April 17, 2026 brings together world-renowned experts for a full day of insights, 
            panels, and networking at Harvard University.
          </p>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="glass-card rounded-2xl p-6 text-center">
              <div className="w-16 h-16 rounded-2xl bg-primary/20 flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-semibold text-xl mb-2">160+</h3>
              <p className="text-muted-foreground">VIP Attendees</p>
            </div>
            <div className="glass-card rounded-2xl p-6 text-center">
              <div className="w-16 h-16 rounded-2xl bg-secondary/20 flex items-center justify-center mx-auto mb-4">
                <GraduationCap className="w-8 h-8 text-secondary" />
              </div>
              <h3 className="font-semibold text-xl mb-2">12+</h3>
              <p className="text-muted-foreground">Expert Speakers</p>
            </div>
            <div className="glass-card rounded-2xl p-6 text-center">
              <div className="w-16 h-16 rounded-2xl bg-primary/20 flex items-center justify-center mx-auto mb-4">
                <Building2 className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-semibold text-xl mb-2">Harvard</h3>
              <p className="text-muted-foreground">Tsai Auditorium</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/agenda"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-primary text-primary-foreground rounded-full font-semibold transition-all hover:-translate-y-1"
            >
              View Full Agenda <ArrowRight className="w-5 h-5" />
            </Link>
            <Link 
              to="/signup"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 glass-card rounded-full font-semibold transition-all hover:bg-white/10"
            >
              Register Now
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto">
          <div className="glass-card rounded-3xl p-12 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-secondary/10" />
            <div className="relative z-10">
              <h2 className="font-outfit text-3xl md:text-4xl font-bold mb-4">
                Join Us in Boston
              </h2>
              <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
                Be part of this historic gathering at the intersection of academia, 
                athletics, and housing policy.
              </p>
              <Link 
                to="/signup"
                className="inline-flex items-center gap-2 px-10 py-5 bg-primary text-primary-foreground rounded-full font-semibold text-lg transition-all hover:-translate-y-1"
              >
                Register for the Experience <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ExperiencePage;
